import {
  computed,
  defineStore,
  storeFactory,
  useSupabase,
  type GetterType,
} from "#imports";
import type { QueryData } from "@supabase/supabase-js";
import { appendComma } from "~/utils/text-formatting";

export type Place = GetterType<ReturnType<typeof usePlaces>["getById"]>;

export const usePlaces = defineStore("places", () => {
  const supabase = useSupabase();

  type RawPlace = QueryData<ReturnType<typeof selectPlaces>>[number];
  const selectPlaces = (withCount: boolean = false) =>
    supabase
      .from("places")
      .select("*", withCount ? { count: "exact" } : undefined)
      .throwOnError();

  // we need to handle each field being the same as another, e.g: Singapore/Singapore/Singapore
  // Truth table: Place/Region/Country => fullname  placeRegion  regionCountry
  // P/R/C  fullname  placeRegion  regionCountry
  // A/B/C  A, B, C   A, B         B, C
  // A/A/C  A, C      A            A, C
  // A/B/B  A, B      A, B         B
  // A/A/A  A, A      A, A         A

  const mapPlace = (place: RawPlace) => ({
    ...place,
    fullName:
      place.place === place.region || place.region === place.country
        ? appendComma(place.place) + place.country
        : appendComma(place.place) + appendComma(place.region) + place.country,
    placeRegion:
      place.place === place.region
        ? place.region
        : appendComma(place.place) + place.region,
    regionCountry:
      place.region === place.country
        ? place.country
        : appendComma(place.region) + place.country,
  });

  const {
    dataById,
    fetchFactory,
    fetchOneFactory,
    fetchById,
    fetchByIds,
    getById,
    getByIds,
    fetchManyByKeysFactory,
  } = storeFactory<RawPlace, ReturnType<typeof mapPlace>>(
    "places",
    async (payload) => await selectPlaces().in("id", payload),
    mapPlace,
  );

  const {
    metadata: metadataCountries,
    fetch: fetchCountries,
    result: countries,
  } = fetchFactory<null>(
    async () =>
      await selectPlaces()
        .is("region", null)
        .is("place", null)
        .order("spot_count", { ascending: false }),
  );
  const countriesWithSpots = computed(() =>
    countries.value?.filter((country) => country.spot_count > 0),
  );

  const {
    metadata: metadataRegions,
    fetch: fetchRegions,
    result: regions,
  } = fetchFactory<null>(
    async () =>
      await selectPlaces()
        .not("region", "is", null)
        .is("place", null)
        .order("spot_count", { ascending: false }),
  );

  const {
    metadata: metadataSearch,
    fetch: search,
    result: searchResults,
    totalRows: searchTotalRows,
  } = fetchFactory<{
    search: string;
    limit?: number;
    offset?: number;
    withTotal?: boolean;
  }>(async (payload) => {
    // sanitize for like queries
    const search = payload.search
      .toLowerCase()
      .replace("_", "")
      .replace("%", "");

    if (search.length === 0) return { data: [], count: 0 };

    return await selectPlaces(payload.withTotal)
      .or(
        `place.ilike.${search}%,region.ilike.${search}%,country.ilike.${search}%`,
      )
      .not("place", "is", null)
      .order("spot_count", { ascending: false })
      .range(
        payload.offset ?? 0,
        (payload.offset ?? 0) + (payload.limit ?? 20) - 1,
      );
  });

  // this will select PLACE by spot count
  const {
    metadata: metadataTopPlaces,
    fetch: fetchTopPlaces,
    result: topPlaces,
  } = fetchFactory<{ limit: number }>(
    async (payload) =>
      await selectPlaces()
        .eq("is_featured", true)
        .not("place", "is", null)
        .order("spot_count", { ascending: false })
        .limit(payload.limit),
  );

  const {
    metadata: metadataByGeohashes,
    fetch: fetchByGeohashes,
    result: placesByGeohashes,
  } = fetchManyByKeysFactory(
    "geohash",
    async (geohashes) =>
      await selectPlaces().not("place", "is", null).in("geohash", geohashes),
  );

  const {
    metadata: metadataAll,
    fetch: fetchAll,
    result: allPlaces,
    totalRows: allTotalRows,
  } = fetchFactory<{
    limit: number;
    offset: number;
    sortBy: string;
    sortDirection?: "asc" | "desc" | null;
  }>(async (payload) => {
    return await selectPlaces(true)
      .order(payload.sortBy, { ascending: payload.sortDirection !== "desc" })
      .order("country")
      .order("region")
      .order("place")
      .not("place", "is", null)
      .range(payload.offset, payload.offset + payload.limit - 1);
  });

  const {
    metadata: metadataPlace,
    fetch: fetchPlace,
    result: place,
  } = fetchOneFactory<{ place: string; region: string; country: string }>(
    async (payload) =>
      await selectPlaces()
        .eq("place", payload.place)
        .eq("region", payload.region)
        .eq("country", payload.country)
        .single(),
  );

  type CountryRegionPlace = {
    country: string;
    region: string | null;
    place: string | null;
  };

  // not the fastest look-up, but will do the job
  const getByCountryRegionPlace = (payload: CountryRegionPlace) =>
    computed(() => {
      for (const [, place] of Object.entries(dataById)) {
        if (payload.place) {
          if (
            payload.place === place.place &&
            payload.region === place.region &&
            payload.country === place.country
          ) {
            return mapPlace(place);
          }
        } else if (payload.region) {
          if (
            payload.region === place.region &&
            payload.country === place.country
          ) {
            return mapPlace(place);
          }
        } else if (payload.country === place.country) {
          return mapPlace(place);
        }
      }
      throw new Error(
        `Unable to getByCountryRegionPlace: ${JSON.stringify(payload)}`,
      );
    });

  return {
    dataById,
    fetchById,
    getById,
    fetchByIds,
    getByIds,

    metadataCountries,
    fetchCountries,
    countries,
    countriesWithSpots,

    metadataRegions,
    fetchRegions,
    regions,

    metadataPlace,
    fetchPlace,
    place,

    metadataSearch,
    search,
    searchResults,
    searchTotalRows,

    metadataTopPlaces,
    fetchTopPlaces,
    topPlaces,

    metadataByGeohashes,
    fetchByGeohashes,
    placesByGeohashes,

    metadataAll,
    fetchAll,
    allPlaces,
    allTotalRows,

    getByCountryRegionPlace,
  };
});
